// src/components/HazardPopup.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HazardItem from "./HazardItem";
import PropTypes from "prop-types";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";

const getHazardPriority = (hazard) => {
  const noMeasure = !hazard.measure || hazard.measure.trim() === "";
  const notControlled = hazard.controlled === false;
  const notEliminated = hazard.eliminated === false;
  return noMeasure && notControlled && notEliminated ? 0 : 1;
};

const getAreaPriority = (hazard) => {
  const areaOrder = { Internal: 0, External: 1 };
  return areaOrder.hasOwnProperty(hazard.area) ? areaOrder[hazard.area] : 2;
};

const HazardPopup = ({
  originalHazards,
  onSave,
  onApply,
  onClose,
  open,
  currentUserId,
}) => {
  const [editedHazards, setEditedHazards] = useState([]);
  const [isEditing, setIsEditing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState("");

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize editedHazards and isEditing when originalHazards change
  useEffect(() => {
    if (originalHazards && originalHazards.length > 0) {
      setIsEditing(originalHazards.map(() => false));
      setEditedHazards(originalHazards.map((hazard) => ({ ...hazard })));
    } else {
      setIsEditing([]);
      setEditedHazards([]);
    }
  }, [originalHazards]);

  // Sorting Logic: useMemo to memoize the sorted hazards
  const sortedHazards = useMemo(() => {
    return [...editedHazards].sort((a, b) => {
      const priorityA = getHazardPriority(a);
      const priorityB = getHazardPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const areaPriorityA = getAreaPriority(a);
      const areaPriorityB = getAreaPriority(b);

      return areaPriorityA - areaPriorityB;
    });
  }, [editedHazards]);

  // Handler to update hazard fields
  const handleInputChange = useCallback((index, field, value) => {
    setEditedHazards((prevHazards) => {
      const updatedHazards = [...prevHazards];
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        updatedHazards[index] = {
          ...updatedHazards[index],
          [parent]: {
            ...updatedHazards[index][parent],
            [child]: value,
          },
        };
      } else {
        updatedHazards[index] = { ...updatedHazards[index], [field]: value };
      }
      return updatedHazards;
    });
  }, []);

  // Handler to toggle edit mode for a specific hazard
  const handleEditToggle = useCallback((index) => {
    setIsEditing((prevEditing) => {
      const updatedEditing = [...prevEditing];
      updatedEditing[index] = !updatedEditing[index];
      return updatedEditing;
    });
  }, []);

  // Handler to remove an image from a hazard
  const handleRemoveImage = useCallback((hazardIndex, imageIndex) => {
    setEditedHazards((prevHazards) => {
      const updatedHazards = [...prevHazards];
      const updatedImages = [
        ...(updatedHazards[hazardIndex].media?.images || []),
      ];
      updatedImages.splice(imageIndex, 1);
      updatedHazards[hazardIndex].media = {
        images: updatedImages,
        permissions: [
          ...(updatedHazards[hazardIndex].media?.permissions || []),
        ],
      };
      return updatedHazards;
    });
  }, []);

  // Handler to delete a hazard
  const handleDelete = useCallback((index) => {
    setEditedHazards((prevHazards) =>
      prevHazards.filter((_, i) => i !== index),
    );
    setIsEditing((prevEditing) => prevEditing.filter((_, i) => i !== index));
  }, []);

  // Function to compare two hazard objects
  const isHazardChanged = useCallback((original, edited) => {
    const fieldsToCompare = [
      "hazard",
      "area",
      "reference",
      "type",
      "eliminated",
      "location",
      "status",
      "frequency",
      "initial_rating",
      "consequence",
      "rating",
      "controlled",
      "controlled_at",
      "measure",
      "corrective_action",
      "internal_description",
    ];

    for (let field of fieldsToCompare) {
      if (original[field] !== edited[field]) {
        return true;
      }
    }

    const originalImages = original.media?.images || [];
    const editedImages = edited.media?.images || [];

    if (originalImages.length !== editedImages.length) {
      return true;
    }

    for (let i = 0; i < originalImages.length; i++) {
      if (originalImages[i] !== editedImages[i]) {
        return true;
      }
    }

    return false;
  }, []);

  // Handler to save changes locally
  const handleSave = useCallback(() => {
    // Identify changed hazards
    const changedHazards = editedHazards.filter((hazard, index) =>
      isHazardChanged(originalHazards[index], hazard),
    );

    if (changedHazards.length === 0) {
      setSaveError("No changes detected to save.");
      return;
    }

    // Save changes locally (e.g., to local state or localStorage)
    onSave(editedHazards);
    setSaveSuccess(true);
    // Optionally, display a success message or perform other actions
  }, [editedHazards, originalHazards, isHazardChanged, onSave]);

  // Handler to apply changes to the backend
  const handleApply = useCallback(async () => {
    if (!onApply) return;

    // Identify changed hazards
    const changedHazards = editedHazards.filter((hazard, index) =>
      isHazardChanged(originalHazards[index], hazard),
    );

    if (changedHazards.length === 0) {
      setApplyError("No changes detected to apply.");
      return;
    }

    setSaving(true);
    setApplyError("");
    try {
      const hazardsToUpdate = changedHazards.map((hazard) => ({
        ...hazard,
        updated_by: currentUserId,
      }));

      const response = await axios.post(
        "https://formbuilderapi.getforsite.com/api/hazards/updateHazards",
        { hazards: hazardsToUpdate },
      );

      if (response.data.success) {
        setApplySuccess(true);
        // Update originalHazards with editedHazards for the changed ones
        const updatedOriginalHazards = [...originalHazards];
        changedHazards.forEach((hazard) => {
          if (!hazard.id) {
            const returnedHazard = response.data.hazards.find(
              (h) =>
                h.hazard === hazard.hazard &&
                h.area === hazard.area &&
                h.reference === hazard.reference,
            );
            if (returnedHazard) {
              const index = updatedOriginalHazards.findIndex(
                (orig) => orig === hazard,
              );
              if (index !== -1) {
                updatedOriginalHazards[index] = {
                  ...hazard,
                  id: returnedHazard.id,
                };
              }
            }
          } else {
            const index = updatedOriginalHazards.findIndex(
              (orig) => orig.id === hazard.id,
            );
            if (index !== -1) {
              updatedOriginalHazards[index] = { ...hazard };
            }
          }
        });
        onApply(updatedOriginalHazards); // Notify parent component of the successful apply
        onClose(); // Close the popup
      } else {
        setApplyError("Failed to apply changes. Please try again.");
      }
    } catch (error) {
      console.error("Error applying changes:", error);
      setApplyError(
        error.response?.data?.error || "An unexpected error occurred.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    editedHazards,
    originalHazards,
    isHazardChanged,
    currentUserId,
    onApply,
    onClose,
  ]);

  // Handler to add a new blank hazard
  const handleAddNew = () => {
    setEditedHazards((prevHazards) => [
      ...prevHazards,
      {
        hazard: "",
        area: "",
        reference: "",
        type: "",
        eliminated: false,
        location: "",
        status: 1, // default active
        frequency: null,
        initial_rating: null,
        consequence: null,
        rating: null,
        controlled: false,
        controlled_at: "",
        measure: "",
        corrective_action: "",
        internal_description: "",
        media: {
          images: [],
          permissions: [],
        },
      },
    ]);
    setIsEditing((prevEditing) => [...prevEditing, true]);
  };

  // Define risk levels and frequency levels
  const riskLevels = useMemo(
    () => [
      { label: "Low", value: 1 },
      { label: "Medium", value: 2 },
      { label: "High", value: 3 },
    ],
    [],
  );

  const frequencyLevels = useMemo(
    () => [
      { label: "Low", value: 1 },
      { label: "Medium", value: 3 },
      { label: "High", value: 5 },
    ],
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
    >
      <DialogTitle>Hazard & Risk Register Audit</DialogTitle>
      <DialogContent dividers>
        {sortedHazards && sortedHazards.length > 0 ? (
          sortedHazards.map((hazard, sortedIndex) => {
            const originalIndex = editedHazards.findIndex(
              (h) =>
                h.id === hazard.id ||
                (h.id === undefined && hazard.id === undefined),
            );

            if (originalIndex === -1) {
              console.warn("Hazard not found in editedHazards:", hazard);
              return null;
            }

            return (
              <HazardItem
                key={hazard.id || `new-hazard-${originalIndex}`}
                hazard={hazard}
                isEditMode={isEditing[originalIndex]}
                onInputChange={handleInputChange}
                onEditToggle={handleEditToggle}
                onRemoveImage={handleRemoveImage}
                onDelete={handleDelete}
                index={originalIndex}
                riskLevels={riskLevels}
                frequencyLevels={frequencyLevels}
                saving={saving}
              />
            );
          })
        ) : (
          <Typography>No Hazards Found for the Site.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Close
        </Button>
        <Button
          onClick={handleAddNew}
          color="primary"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={saving}
        >
          Add New
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess || applySuccess}
        autoHideDuration={6000}
        onClose={() => {
          setSaveSuccess(false);
          setApplySuccess(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setSaveSuccess(false);
            setApplySuccess(false);
          }}
          severity="success"
          sx={{ width: "100%" }}
        >
          {saveSuccess
            ? "Hazards saved locally!"
            : "Changes applied successfully!"}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={Boolean(saveError) || Boolean(applyError)}
        autoHideDuration={6000}
        onClose={() => {
          setSaveError("");
          setApplyError("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setSaveError("");
            setApplyError("");
          }}
          severity="error"
          sx={{ width: "100%" }}
        >
          {saveError || applyError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

HazardPopup.propTypes = {
  originalHazards: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired, // Function to update parent state after saving locally
  onApply: PropTypes.func, // Optional: Function to apply changes to backend
  onClose: PropTypes.func.isRequired, // Function to close the popup
  open: PropTypes.bool.isRequired, // Controls whether the popup is open
  currentUserId: PropTypes.number.isRequired, // ID of the current user making the update
};

export default React.memo(HazardPopup);
